/**
 * Encouragement: the tier words, and the bank of things you may say.
 *
 * Two rules from the prototype are load-bearing and are implemented here:
 *
 *  1. A slider resolves to a WORD, never a number. Nobody is ever shown a
 *     figure -- not the rater, not the writer.
 *  2. Comments are CHOSEN, not typed. Picking from a bank written in advance
 *     makes cruelty structurally unavailable and removes a moderation surface
 *     entirely. The one exception is verified church staff, whose written note
 *     is moderated before the writer sees it.
 *
 * Chip keys are stored, not sentences, so this wording can be improved later
 * without putting different words in a past rater's mouth.
 */

export const TIERS = ['Not for me', 'Needs work', 'Developing', 'Good', 'Excellent']

export function tierLabel(v) {
  if (v < 20) return TIERS[0]
  if (v < 40) return TIERS[1]
  if (v < 60) return TIERS[2]
  if (v < 80) return TIERS[3]
  return TIERS[4]
}

const tierKey = (v) => (v < 40 ? 'low' : v < 70 ? 'mid' : 'high')

/**
 * Below this average the rater is thanked and sent on their way without being
 * offered any comments at all. A low rating still counts as a listen; it just
 * never travels to the writer as criticism.
 */
export const WARMTH_BELOW = 40

/**
 * The fourth question depends on who is asking, and that is not a choice --
 * see my_rater_kind() in the database. A listener is asked about the music; a
 * verified staff member is asked whether a room could sing it.
 */
export const DIMENSIONS = {
  listener: [
    ['overall', 'Overall'],
    ['lyrics', 'Lyrics'],
    ['theology', 'Theology'],
    ['fourth', 'Musicality'],
  ],
  pastor: [
    ['overall', 'Overall'],
    ['lyrics', 'Lyrics'],
    ['theology', 'Theology'],
    ['fourth', 'Congregational fit'],
  ],
}

const BANK = {
  overall: {
    low: [
      "This isn't quite ready for a congregation yet, but it's close in places.",
      "You're clearly growing as a writer — keep at it.",
      'This feels like a first draft of something that could be strong.',
    ],
    mid: [
      'Good bones — the finishing work is what’s next.',
      "There's something good in here, but it hasn't fully arrived yet.",
      'A solid attempt — a rewrite of the middle section would help a lot.',
    ],
    high: [
      'This came through as a genuine offering — thank you for writing it.',
      'Confident writing — the idea and the execution match.',
      'This is the kind of song that gets better the more you sing it.',
    ],
  },
  lyrics: {
    low: [
      'Right now the words are closer to notes-to-self than a finished lyric.',
      'A few lines feel rushed — worth slowing down on the language.',
      'Worth reading these out loud without music to hear where they stumble.',
    ],
    mid: [
      'A few lines feel unfinished or placeholder-ish — worth another pass.',
      'The wording occasionally gets in the way of the message.',
      'Strong images in places, but the through-line gets lost between them.',
    ],
    high: [
      'Every line earns its place — nothing feels wasted.',
      'Singable and memorable from the first pass.',
      'The words carry the melody instead of fighting it.',
    ],
  },
  theology: {
    low: [
      'The message wanders theologically rather than staying with one true thing.',
      "Worth asking: what is this song actually claiming about God? That answer isn't clear yet.",
      'A pass with a pastor or theologically trained friend would help a lot here.',
    ],
    mid: [
      'Worth checking a couple of lines against scripture — the theology drifts in places.',
      'The song starts in one theological direction and ends in another.',
      'Solid instinct, but a theology check would sharpen this considerably.',
    ],
    high: [
      'Doctrinally sound and clearly rooted in scripture.',
      'A thoughtful, biblically grounded take on the theme.',
      'This says something true about God in a fresh way.',
    ],
  },
  // Listener's fourth question.
  musicality: {
    low: [
      'The melody is hard to hold onto after one listen.',
      'Right now the music feels like a backdrop rather than a partner to the lyric.',
      "Worth trying a different key or simpler chords to find the song's natural voice.",
    ],
    mid: [
      "A pleasant melody, though it doesn't yet do anything unexpected.",
      'The chord progression is safe — one surprising choice could lift this.',
      'A stronger hook in the chorus would help this stick.',
    ],
    high: [
      'The melody is memorable after just one listen.',
      'Chord choices serve the lyric instead of just filling space.',
      "This has a musical identity of its own — it doesn't sound like a copy of anything else.",
    ],
  },
  // Church staff's fourth question. A different question needs different
  // answers: this one is about a room full of people, not about craft.
  fit: {
    low: [
      'Hard for a congregation to join in on as it stands.',
      'The range sits where most of a room would struggle.',
      'This reads more as a song to listen to than one to sing together.',
    ],
    mid: [
      'Consider a lower key for congregations.',
      'The verse is singable; the chorus asks a lot of an untrained room.',
      'Close — a simpler melodic shape in one section would open this up.',
    ],
    high: [
      "We'd sing this at our church.",
      'A room would pick this up by the second chorus.',
      'Sits well in a congregational range without losing its character.',
    ],
  },
}

/**
 * The chips offered for one set of answers, as {key, text}.
 * Keys are stable and stored; the text is display only.
 */
export function chipsFor(values, raterKind) {
  const fourth = raterKind === 'pastor' ? 'fit' : 'musicality'
  const cats = [
    ['overall', 'overall', values.overall],
    ['lyrics', 'lyrics', values.lyrics],
    ['theology', 'theology', values.theology],
    [fourth, 'fourth', values.fourth],
  ]
  const out = []
  for (const [bankName, , value] of cats) {
    const bucket = tierKey(value)
    BANK[bankName][bucket].forEach((text, i) => {
      out.push({ key: `${bankName}.${bucket}.${i}`, text })
    })
  }
  return out
}

/** Resolve a stored key back to its sentence, for showing a writer. */
export function chipText(key) {
  const [cat, bucket, i] = (key ?? '').split('.')
  return BANK[cat]?.[bucket]?.[Number(i)] ?? null
}

export const averageOf = (v) =>
  (v.overall + v.lyrics + v.theology + v.fourth) / 4
